import { useState, useRef, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Send,
  Sparkles,
  AlertCircle,
  Wrench,
  Paperclip,
  X,
  FileText,
  FileType,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/cn';

interface PendingAttachment {
  id: string;
  file: File;
  previewUrl?: string;
}

interface SentAttachment {
  filename: string;
  mimeType: string;
  previewUrl?: string;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  attachments?: SentAttachment[];
  toolCalls?: Array<{ name: string; result: unknown }>;
}

const ACCEPTED_MIMES = new Set([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'application/pdf',
  'text/plain',
  'text/markdown',
  'text/csv',
  'application/json',
]);
const MAX_ATTACHMENTS = 4;
const MAX_BYTES = 10 * 1024 * 1024;

function AttachmentChip({
  filename,
  mimeType,
  previewUrl,
  size = 'sm',
}: {
  filename: string;
  mimeType: string;
  previewUrl?: string;
  size?: 'sm' | 'lg';
}) {
  const isImage = mimeType.startsWith('image/');
  const isPdf = mimeType === 'application/pdf';
  const dim = size === 'lg' ? 'h-16 w-16' : 'h-10 w-10';
  if (isImage && previewUrl) {
    return (
      <img
        src={previewUrl}
        alt={filename}
        className={cn(dim, 'rounded-md border border-border object-cover')}
        title={filename}
      />
    );
  }
  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border border-border bg-background/60 px-2 py-1 text-[11px] text-muted-foreground',
      )}
      title={filename}
    >
      {isPdf ? <FileType className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
      <span className="max-w-[140px] truncate">{filename}</span>
    </div>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // result is "data:<mime>;base64,<payload>"
      const payload = result.includes(',') ? result.slice(result.indexOf(',') + 1) : result;
      resolve(payload);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });
}

const SUGGESTED_CHIPS = [
  "what's blocking me",
  "what's overdue to order",
  "what's still in PLANNING",
  "what have I spent in this room",
];

interface Props {
  projectId: string;
}

export function RenovationChat({ projectId }: Props) {
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState<string | undefined>();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages]);

  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);

  // Revoke object URLs on unmount / when pending changes.
  useEffect(() => {
    return () => {
      for (const p of pending) {
        if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const incoming = Array.from(files);
      setPending((curr) => {
        const slots = MAX_ATTACHMENTS - curr.length;
        if (slots <= 0) {
          toast.error(`Max ${MAX_ATTACHMENTS} attachments per turn.`);
          return curr;
        }
        const accepted: PendingAttachment[] = [];
        for (const f of incoming.slice(0, slots)) {
          if (!ACCEPTED_MIMES.has(f.type)) {
            toast.error(`${f.name}: unsupported file type (${f.type || 'unknown'}).`);
            continue;
          }
          if (f.size > MAX_BYTES) {
            toast.error(`${f.name}: too large (max 10 MB).`);
            continue;
          }
          accepted.push({
            id: crypto.randomUUID(),
            file: f,
            previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined,
          });
        }
        if (incoming.length > slots) {
          toast.error(`Only ${slots} more attachment slot(s) available; extras skipped.`);
        }
        return [...curr, ...accepted];
      });
    },
    [],
  );

  const removePending = (id: string) => {
    setPending((curr) => {
      const target = curr.find((p) => p.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return curr.filter((p) => p.id !== id);
    });
  };

  const chat = useMutation({
    mutationFn: async (args: { message: string; pending: PendingAttachment[] }) => {
      const attachments = await Promise.all(
        args.pending.map(async (p) => ({
          filename: p.file.name,
          mimeType: p.file.type,
          contentBase64: await fileToBase64(p.file),
        })),
      );
      return api.renoChat(projectId, {
        message: args.message,
        sessionId,
        attachments: attachments.length > 0 ? attachments : undefined,
      });
    },
    onSuccess: (res) => {
      setSessionId(res.sessionId);
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: res.reply,
          toolCalls: res.toolCalls.map((t) => ({ name: t.name, result: t.result })),
        },
      ]);
      // Refetch grid if mutations happened
      if (res.toolCalls.some((t) => t.name !== 'query_renovation_state')) {
        void queryClient.invalidateQueries({ queryKey: qk.renovation(projectId) });
      }
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : 'Chat failed.';
      toast.error(msg);
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: 'assistant', content: `Error: ${msg}` },
      ]);
    },
  });

  const submit = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed && pending.length === 0) return;
    // Echo into the transcript optimistically. We capture the previewUrls so
    // image thumbnails show on the user bubble after send. (URLs are revoked
    // on unmount; for in-session display they remain valid.)
    const sentAttachments: SentAttachment[] = pending.map((p) => ({
      filename: p.file.name,
      mimeType: p.file.type,
      previewUrl: p.previewUrl,
    }));
    setMessages((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        role: 'user',
        content: trimmed,
        attachments: sentAttachments.length > 0 ? sentAttachments : undefined,
      },
    ]);
    setInput('');
    const sending = pending;
    setPending([]);
    chat.mutate({ message: trimmed || '(see attachment)', pending: sending });
  };

  return (
    <Card className="flex h-full flex-col">
      <CardContent className="flex min-h-0 flex-1 flex-col gap-3 p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-medium">Renovation chat</h2>
          </div>
          {sessionId && (
            <button
              onClick={() => {
                setSessionId(undefined);
                setMessages([]);
              }}
              className="text-[11px] text-muted-foreground hover:text-foreground"
            >
              new session
            </button>
          )}
        </div>

        <div
          ref={scrollRef}
          className="min-h-0 flex-1 space-y-3 overflow-y-auto rounded-md border border-border bg-background/40 p-3"
        >
          {messages.length === 0 ? (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Talk to the grid. Add rows, change statuses, log expenses, ask for status. Use the
                chips below to start, or type freely.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {SUGGESTED_CHIPS.map((c) => (
                  <button
                    key={c}
                    onClick={() => submit(c)}
                    className="rounded-md border border-border px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground"
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <AnimatePresence initial={false}>
              {messages.map((m) => (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ type: 'spring', stiffness: 140, damping: 16 }}
                  className={cn(
                    'flex',
                    m.role === 'user' ? 'justify-end' : 'justify-start',
                  )}
                >
                  <div
                    className={cn(
                      'max-w-[85%] rounded-md px-3 py-2 text-sm',
                      m.role === 'user'
                        ? 'bg-accent text-accent-foreground'
                        : 'bg-muted text-foreground',
                    )}
                  >
                    {m.attachments && m.attachments.length > 0 && (
                      <div className="mb-1.5 flex flex-wrap gap-1.5">
                        {m.attachments.map((a, i) => (
                          <AttachmentChip key={i} filename={a.filename} mimeType={a.mimeType} previewUrl={a.previewUrl} />
                        ))}
                      </div>
                    )}
                    <p className="whitespace-pre-wrap">{m.content}</p>
                    {m.toolCalls && m.toolCalls.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {m.toolCalls.map((t, i) => {
                          const errored =
                            typeof t.result === 'object' &&
                            t.result !== null &&
                            'error' in (t.result as Record<string, unknown>);
                          return (
                            <Badge
                              key={i}
                              variant={errored ? 'danger' : 'muted'}
                              className="text-[10px]"
                            >
                              {errored ? (
                                <AlertCircle className="mr-1 h-2.5 w-2.5" />
                              ) : (
                                <Wrench className="mr-1 h-2.5 w-2.5" />
                              )}
                              {t.name}
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          )}
          {chat.isPending && (
            <div className="flex justify-start">
              <div className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <span className="animate-pulse">thinking</span>
                  <span className="animate-pulse delay-100">.</span>
                  <span className="animate-pulse delay-200">.</span>
                  <span className="animate-pulse delay-300">.</span>
                </span>
              </div>
            </div>
          )}
        </div>

        {pending.length > 0 && (
          <div className="flex flex-wrap gap-2 rounded-md border border-dashed border-border bg-background/40 p-2">
            {pending.map((p) => (
              <div key={p.id} className="group relative">
                <AttachmentChip
                  filename={p.file.name}
                  mimeType={p.file.type}
                  previewUrl={p.previewUrl}
                  size="lg"
                />
                <button
                  type="button"
                  onClick={() => removePending(p.id)}
                  className="absolute -right-1 -top-1 rounded-full border border-border bg-background p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100"
                  title="Remove"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div
          className={cn(
            'flex items-end gap-2 rounded-md transition-colors',
            dragOver && 'ring-2 ring-primary/40',
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,text/plain,text/markdown,text/csv,application/json"
            className="hidden"
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
          />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => fileInputRef.current?.click()}
            disabled={chat.isPending || pending.length >= MAX_ATTACHMENTS}
            title="Attach files (images / PDF / text)"
            className="self-end"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onPaste={(e) => {
              // Clipboard image (e.g. screenshot paste) → treat as attachment.
              const files: File[] = [];
              for (const item of e.clipboardData.items) {
                if (item.kind === 'file') {
                  const f = item.getAsFile();
                  if (f) files.push(f);
                }
              }
              if (files.length > 0) {
                e.preventDefault();
                addFiles(files);
              }
            }}
            placeholder={'e.g. "add tilework to kitchen, est $4500", paste a URL, or drop an image'}
            className="min-h-[44px] flex-1 resize-none"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit(input);
              }
            }}
            disabled={chat.isPending}
          />
          <Button
            size="sm"
            onClick={() => submit(input)}
            disabled={chat.isPending || (!input.trim() && pending.length === 0)}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
